// src/Components/Promo/PortfolioList.js

import React, { useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Box,
  CircularProgress,
  Typography,
  FormControlLabel,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';

const ORANGE_COLOR = '#FF9900';
const DARK_BLUE = '#00008B';
const LIGHT_BLUE_BG = '#E3F2FD';

const formatPubDate = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  if (isNaN(date)) return '-';
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
};

const EditableCell = ({
  value,
  isEditing,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  placeholder,
  sx = {},
}) => {
  return (
    <TextField
      value={value}
      autoFocus={autoFocus}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value, e)}
      onClick={(e) => e.stopPropagation()}
      onFocus={onFocus}
      onBlur={onBlur}
      variant='standard'
      size='small'
      fullWidth
      InputProps={{
        disableUnderline: true,
        style: { fontSize: '0.8rem', color: DARK_BLUE, fontWeight: 'normal' },
      }}
      sx={{
        backgroundColor: LIGHT_BLUE_BG,
        px: 1,
        borderRadius: 1,
        border: `1px solid ${ORANGE_COLOR}`,
        boxSizing: 'border-box',
        '& .MuiInputBase-root': {
          height: '100%',
          fontSize: '0.8rem',
          color: DARK_BLUE,
        },
        '& .MuiInputBase-input': {
          color: DARK_BLUE,
          fontWeight: 'normal',
        },
        '&:hover': { backgroundColor: LIGHT_BLUE_BG },
        ...sx,
      }}
    />
  );
};

export default function PortfolioList({
  loading,
  displayRows,
  selectedIds,
  focusedRowId,
  actions,
  mode,
  outputFormats,
  setOutputFormats,
  generatingRowIds,
}) {
  const [focusedField, setFocusedField] = useState(null);

  if (loading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );

  const rowCount = displayRows.length;
  const numSelected = selectedIds.length;
  const isAllSelected = rowCount > 0 && numSelected === rowCount;
  const isIndeterminate = numSelected > 0 && numSelected < rowCount;

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#fff' }}>
      <Box
        sx={{
          px: 2,
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          boxSizing: 'border-box',
        }}>
        {mode !== 'NEWSFEED' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: DARK_BLUE }}>
              Output Formats:
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size='small'
                    checked={outputFormats?.generateWord || false}
                    sx={{ p: 0.5, '&.Mui-checked': { color: 'primary.main' } }}
                    onChange={(e) =>
                      setOutputFormats((prev) => ({
                        ...prev,
                        generateWord: e.target.checked,
                      }))
                    }
                  />
                }
                label={
                  <Typography variant='body2' color='textPrimary'>
                    Word
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size='small'
                    checked={outputFormats?.generatePdf || false}
                    sx={{ p: 0.5, '&.Mui-checked': { color: 'primary.main' } }}
                    onChange={(e) =>
                      setOutputFormats((prev) => ({
                        ...prev,
                        generatePdf: e.target.checked,
                      }))
                    }
                  />
                }
                label={
                  <Typography variant='body2' color='textPrimary'>
                    PDF
                  </Typography>
                }
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} />
        )}

        <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 1 }}>
          <Tooltip
            title='Copy Settings'
            placement='top'
            arrow
            slotProps={{ popper: { sx: { zIndex: 100020 } } }}>
            <span>
              <IconButton
                size='small'
                onClick={() => actions.handleCopy && actions.handleCopy()}
                disabled={numSelected !== 1}
                sx={{ p: 0.25 }}>
                <ContentCopyIcon
                  sx={{ fontSize: '24px', color: numSelected === 1 ? '#2e7d32' : 'grey' }}
                />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title='Paste as New Copy'
            placement='top'
            arrow
            slotProps={{ popper: { sx: { zIndex: 100020 } } }}>
            <span>
              <IconButton
                size='small'
                onClick={() => actions.handlePaste && actions.handlePaste()}
                disabled={!actions.hasCopiedData}
                sx={{ p: 0.25 }}>
                <ContentPasteIcon
                  sx={{
                    fontSize: '24px',
                    color: actions.hasCopiedData ? '#2e7d32' : 'grey',
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          flexGrow: 1,
          width: '100%',
          boxSizing: 'border-box',
          borderTop: '2px solid #ff9800',
          borderBottom: 'none',
          borderRadius: 0,
          overflowY: 'auto',
        }}>
        <Table stickyHeader size='small' sx={{ width: '100%' }}>
          <TableHead>
            <TableRow
              sx={{
                height: '40px',
                '& th': {
                  backgroundColor: '#fff3e0',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #ff9800',
                  py: 0,
                },
              }}>
              <TableCell padding='checkbox' sx={{ width: '60px', textAlign: 'center' }}>
                {mode !== 'NEWSFEED' && (
                  <Checkbox
                    color='primary'
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={(e) => actions.handleSelectAll(e.target.checked)}
                    size='small'
                    sx={{ p: 0.5 }}
                  />
                )}
              </TableCell>
              <TableCell>
                {mode === 'NEWSFEED' ? 'Newsfeed Name' : 'Portfolio Name'}
              </TableCell>
              {mode === 'NEWSFEED' && <TableCell>Subject Title</TableCell>}
              {mode === 'NEWSFEED' && (
                <TableCell sx={{ width: '80px', textAlign: 'center' }}>Markers</TableCell>
              )}
              {mode === 'NEWSFEED' && (
                <TableCell sx={{ width: '100px', textAlign: 'center' }}>
                  No. Broadcast
                </TableCell>
              )}
              {mode === 'NEWSFEED' && (
                <TableCell sx={{ width: '80px', textAlign: 'center' }}>Hits</TableCell>
              )}
              {mode === 'NEWSFEED' && (
                <TableCell sx={{ width: '100px', textAlign: 'center' }}>
                  Published
                </TableCell>
              )}
              {mode === 'NEWSFEED' && (
                <TableCell sx={{ width: '100px', textAlign: 'center' }}>Date</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.map((row) => {
              const isSelected = selectedIds.includes(row.id);
              const isPublished = !!(
                (row.pdfDownloadUrl || row.pdfStoragePath) &&
                (row.pdfThumbnailUrl || row.thumbStoragePath) &&
                (row.hiresDownloadUrl || row.hiresStoragePath)
              );

              return (
                <TableRow
                  key={row.id}
                  hover
                  selected={isSelected}
                  onClick={() => actions.handleCheckboxChange(row.id)}
                  sx={{
                    cursor: 'pointer',
                    '&.Mui-selected': { backgroundColor: 'transparent !important' },
                    '&.Mui-selected:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                    },
                  }}>
                  <TableCell padding='checkbox' sx={{ textAlign: 'center' }}>
                    <Checkbox
                      checked={isSelected}
                      size='small'
                      sx={{
                        pointerEvents: 'none',
                        '&.Mui-checked': { color: 'primary.main' },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{ cursor: 'text' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      actions.setFocusedRowId(row.id);
                      setFocusedField('name');
                    }}>
                    <EditableCell
                      value={row.name}
                      isEditing={true}
                      autoFocus={focusedRowId === row.id && focusedField === 'name'}
                      onFocus={() => {
                        actions.setFocusedRowId(row.id);
                        setFocusedField('name');
                      }}
                      onBlur={() => {
                        actions.setFocusedRowId(null);
                        setFocusedField(null);
                      }}
                      onChange={(val, e) =>
                        actions.handleFieldChange(row.id, 'name', val, e)
                      }
                      placeholder='Enter Name'
                    />
                  </TableCell>
                  {mode === 'NEWSFEED' && (
                    <TableCell
                      sx={{ cursor: 'text' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        actions.setFocusedRowId(row.id);
                        setFocusedField('subjectTitle');
                      }}>
                      <EditableCell
                        value={row.subjectTitle || ''}
                        isEditing={true}
                        autoFocus={
                          focusedRowId === row.id && focusedField === 'subjectTitle'
                        }
                        onFocus={() => {
                          actions.setFocusedRowId(row.id);
                          setFocusedField('subjectTitle');
                        }}
                        onBlur={() => {
                          actions.setFocusedRowId(null);
                          setFocusedField(null);
                        }}
                        onChange={(val, e) =>
                          actions.handleFieldChange(row.id, 'subjectTitle', val, e)
                        }
                        placeholder='Enter Subject Title'
                      />
                    </TableCell>
                  )}
                  {mode === 'NEWSFEED' && (
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Typography variant='body2' sx={{ color: DARK_BLUE }}>
                        {generatingRowIds?.includes(row.id)
                          ? ''
                          : row.markerCount != null
                            ? row.markerCount
                            : '-'}
                      </Typography>
                    </TableCell>
                  )}
                  {mode === 'NEWSFEED' && (
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Typography variant='body2' sx={{ color: DARK_BLUE }}>
                        {row.broadcastCount || 0}
                      </Typography>
                    </TableCell>
                  )}
                  {mode === 'NEWSFEED' && (
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Typography variant='body2' sx={{ color: DARK_BLUE }}>
                        {row.hitCount || 0}
                      </Typography>
                    </TableCell>
                  )}
                  {mode === 'NEWSFEED' && (
                    <TableCell sx={{ textAlign: 'center' }}>
                      {isPublished ? (
                        <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1.2rem' }} />
                      ) : (
                        <CancelIcon sx={{ color: '#f44336', fontSize: '1.2rem' }} />
                      )}
                    </TableCell>
                  )}
                  {mode === 'NEWSFEED' && (
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Typography variant='body2' sx={{ color: DARK_BLUE }}>
                        {formatPubDate(row.PublishedDate)}
                      </Typography>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
